import { getWorkflowOffset, OffsetUnit } from "@sp-crm/core";
import {
    AdvancedSearchEntityType,
    CustomListKey,
    GetWorkflowSettingsResult,
    WorkflowEventType,
} from "generated/graphql";
import { EditableWorkflow } from "./types";

export function customListKeyForEntityType(
    attachTo: AdvancedSearchEntityType,
): CustomListKey {
    switch (attachTo) {
        case AdvancedSearchEntityType.HomeCareLead:
            return CustomListKey.HomeCareLeadStatus;
        default:
            return CustomListKey.ClientStatus;
    }
}

export function entityTypeNoun(attachTo: AdvancedSearchEntityType): string {
    switch (attachTo) {
        case AdvancedSearchEntityType.HomeCareLead:
            return "home care client";
        case AdvancedSearchEntityType.ReferenceBusiness:
            return "referral organization";
        case AdvancedSearchEntityType.ReferenceContact:
            return "referral contact";
        default:
            return "client";
    }
}

export function entityTypeLabel(attachTo: AdvancedSearchEntityType): string {
    switch (attachTo) {
        case AdvancedSearchEntityType.HomeCareLead:
            return "Home Care Client";
        case AdvancedSearchEntityType.ReferenceBusiness:
            return "Referral Organization";
        case AdvancedSearchEntityType.ReferenceContact:
            return "Referral Contact";
        default:
            return "Client";
    }
}

const CLIENT_EMAIL_RECIPIENT_TOKENS = [
    { label: "Client email", token: "{{client.email}}" },
    { label: "Best contact email", token: "{{client.bestContactEntity.email1}}" },
    {
        label: "Referral contact email",
        token: "{{client.referralContact.contactEntity.email1}}",
    },
    { label: "Chosen community email", token: "{{client.chosenCommunity.email}}" },
    {
        label: "Chosen community primary contact emails",
        token: "{{client.chosenCommunity.primaryContactEmails}}",
    },
];

const HOME_CARE_LEAD_EMAIL_RECIPIENT_TOKENS = [
    {
        label: "Home care client email",
        token: "{{homeCareLead.selfContactEntity.email1}}",
    },
    {
        label: "Best contact email",
        token: "{{homeCareLead.bestContactEntity.email1}}",
    },
    {
        label: "Referral contact email",
        token: "{{homeCareLead.referralContact.contactEntity.email1}}",
    },
];

const REFERENCE_CONTACT_EMAIL_RECIPIENT_TOKENS = [
    {
        label: "Referral contact email",
        token: "{{referenceContact.contactEntity.email1}}",
    },
];

const REFERENCE_BUSINESS_EMAIL_RECIPIENT_TOKENS = [
    {
        label: "Referral organization email",
        token: "{{referenceBusiness.email}}",
    },
];

export function getEmailRecipientTokens(
    attachTo: AdvancedSearchEntityType,
): { label: string; token: string }[] {
    switch (attachTo) {
        case AdvancedSearchEntityType.HomeCareLead:
            return HOME_CARE_LEAD_EMAIL_RECIPIENT_TOKENS;
        case AdvancedSearchEntityType.ReferenceContact:
            return REFERENCE_CONTACT_EMAIL_RECIPIENT_TOKENS;
        case AdvancedSearchEntityType.ReferenceBusiness:
            return REFERENCE_BUSINESS_EMAIL_RECIPIENT_TOKENS;
        default:
            return CLIENT_EMAIL_RECIPIENT_TOKENS;
    }
}

export const resolveWorkflowSetting = (
    workflowSettings: GetWorkflowSettingsResult,
    workflow: EditableWorkflow,
    settingKey: string,
): string | null => {
    const tenantValue = workflowSettings.tenant.find(
        s => s.key === settingKey && s.workflowId === workflow.id,
    )?.textValue;
    const userValue = workflowSettings.user.find(
        s => s.key === settingKey && s.workflowId === workflow.id,
    )?.textValue;
    const defaultSetting = workflow.settings?.find(s => s.key === settingKey);
    return tenantValue || userValue || defaultSetting?.defaultValue || null;
};

const isStatusFieldName = (fieldName: string) =>
    fieldName === "status" || fieldName === "statusListItem";

export const hasEmailOpenedTrigger = (workflow: EditableWorkflow): boolean => {
    return workflow.trigger?.eventType === WorkflowEventType.EmailOpened;
};

export const hasScheduleTrigger = (workflow: EditableWorkflow): boolean => {
    return workflow.trigger?.eventType === WorkflowEventType.Schedule;
};

export const hasPipelineStageChangedTrigger = (workflow: EditableWorkflow): boolean => {
    return workflow.trigger?.eventType === WorkflowEventType.PipelineStageChanged;
};

export const findFieldMatcher = (workflow: EditableWorkflow, fieldName: string) => {
    return workflow?.trigger?.entityUpdated?.fieldMatchers?.find(
        m => m.fieldName === fieldName,
    );
};

export const hasFieldMatcher = (
    workflow: EditableWorkflow,
    fieldName: string,
): boolean => {
    return !!findFieldMatcher(workflow, fieldName);
};

export const hasReferralSourceTrigger = (workflow: EditableWorkflow) => {
    return hasFieldMatcher(workflow, "referralContactId");
};

export const hasMoveInTrigger = (workflow: EditableWorkflow) => {
    return hasFieldMatcher(workflow, "moveTimelineActual");
};

export const hasCareStartTrigger = (workflow: EditableWorkflow) => {
    return hasFieldMatcher(workflow, "actualStartDate");
};

export const hasCheckLaterTrigger = (workflow: EditableWorkflow) => {
    return hasFieldMatcher(workflow, "statusCheckLaterDate");
};

export const hasAddedToOrganizationTrigger = (workflow: EditableWorkflow) => {
    return hasFieldMatcher(workflow, "businessId");
};

export const findClientStatusFieldMatcher = (workflow: EditableWorkflow) => {
    return (
        workflow?.trigger?.entityUpdated?.fieldMatchers?.find(
            matcher =>
                isStatusFieldName(matcher.fieldName) &&
                Array.isArray(matcher.current?.values) &&
                !matcher.previous?.values &&
                !matcher.previous?.specialValue,
        ) || null
    );
};

export const hasClientStatusTrigger = (workflow: EditableWorkflow) => {
    return !!findClientStatusFieldMatcher(workflow);
};

const findInactiveClientStatusCondition = (workflow: EditableWorkflow) => {
    const entityCondition = workflow?.trigger?.schedule?.entityCondition;

    if (!entityCondition || entityCondition.nodeType !== "And") {
        return null;
    }

    return (
        entityCondition.children?.find(
            condition =>
                condition.nodeType === "Or" &&
                condition.children?.every(
                    c =>
                        c.nodeType === "FieldCondition" &&
                        c.fieldName === "statusListItem",
                ),
        ) || null
    );
};

const findInactiveClientDateCondition = (workflow: EditableWorkflow) => {
    const entityCondition = workflow?.trigger?.schedule?.entityCondition;

    if (!entityCondition || entityCondition.nodeType !== "And") {
        return null;
    }

    return (
        entityCondition.children?.find(
            condition =>
                condition.nodeType === "FieldCondition" &&
                condition.fieldName === "updatedAt" &&
                typeof condition.relativeDate?.value === "number",
        ) || null
    );
};

export const hasInactiveClientTrigger = (workflow: EditableWorkflow) => {
    return (
        !!findInactiveClientStatusCondition(workflow) &&
        !!findInactiveClientDateCondition(workflow)
    );
};

export const hasSingleEmailAction = (workflow: EditableWorkflow) => {
    return (
        workflow?.actions?.length === 1 &&
        workflow.actions[0].__typename === "WorkflowActionEmailEntity"
    );
};

export const hasSingleTaskAction = (workflow: EditableWorkflow) => {
    return (
        workflow?.actions?.length === 1 &&
        workflow.actions[0].__typename === "WorkflowActionTaskEntity"
    );
};

export const hasEditableDelay = (workflow: EditableWorkflow): boolean => {
    if (hasEmailOpenedTrigger(workflow) || hasScheduleTrigger(workflow)) {
        return false;
    }

    return true;
};

export const computeInitialDelay = (seconds: number): [number, OffsetUnit] => {
    if (!seconds) {
        return [0, "m"];
    }

    if (seconds < 60) {
        return [0, "m"];
    }

    if (seconds < 60 * 60) {
        return [Math.floor(seconds / 60), "m"];
    }

    if (seconds < 60 * 60 * 24) {
        return [Math.floor(seconds / 60 / 60), "h"];
    }

    return [seconds / 60 / 60 / 24, "d"];
};

export const isTokenDelayRecipe = (delayRecipe: string | null): boolean =>
    !!delayRecipe?.includes("{{");

export const computeWorkflowDelay = (
    workflow: Pick<EditableWorkflow, "delay" | "delayRecipe">,
): [number, OffsetUnit] => {
    if (isTokenDelayRecipe(workflow.delayRecipe)) {
        return [0, "m"];
    }

    if (workflow.delayRecipe) {
        return getWorkflowOffset(workflow.delayRecipe);
    }

    return computeInitialDelay(workflow.delay);
};

export const findEmailOpenedStatusFieldMatcher = (workflow: EditableWorkflow) => {
    return workflow?.trigger?.emailOpened?.entityFieldMatchers?.find(
        m => m.fieldName === "statusListItem",
    );
};

const findInactiveClientStatuses = (workflow: EditableWorkflow): string[] => {
    const statusCondition = findInactiveClientStatusCondition(workflow);

    if (!statusCondition) {
        return [];
    }

    return statusCondition.children
        .filter(c => c.nodeType === "FieldCondition" && c.fieldName === "statusListItem")
        .map(c => c.textValue);
};

export const findInactiveClientDays = (workflow: EditableWorkflow): number => {
    const entityCondition = workflow?.trigger?.schedule?.entityCondition;

    if (entityCondition?.nodeType !== "And") {
        return 0;
    }

    const dateCondition = entityCondition.children?.find(
        condition =>
            condition.nodeType === "FieldCondition" &&
            condition.fieldName === "updatedAt" &&
            typeof condition.relativeDate?.value === "number",
    );

    if (!dateCondition) {
        return 0;
    }

    return Math.abs(dateCondition.relativeDate.value);
};

export const getWorkflowStatusIds = (workflow: EditableWorkflow): string[] => {
    if (hasClientStatusTrigger(workflow)) {
        const matcher = findClientStatusFieldMatcher(workflow);
        return matcher?.current?.values || [];
    }

    if (hasEmailOpenedTrigger(workflow)) {
        const matcher = findEmailOpenedStatusFieldMatcher(workflow);
        return matcher?.current?.values || [];
    }

    if (hasInactiveClientTrigger(workflow)) {
        return findInactiveClientStatuses(workflow);
    }

    if (hasMoveInTrigger(workflow) || hasCheckLaterTrigger(workflow)) {
        const statusMatcher = findClientStatusFieldMatcher(workflow);
        return statusMatcher?.current?.values || [];
    }

    return [];
};
